import { useMemo } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';

import { fg } from '@confluence/feature-gating';
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import {
	calculateEstimatedReadTime,
	getAdf,
	getAdfString,
	getWordCount,
	readTimeFromFeatures,
} from '../readTimeUtils.ts';
import type { ContentFeatures } from '../featuresExtractor.ts';
import {
	extractAllContentFeatures,
	extractContentFeatures,
	extractContentFeaturesSkipExpand,
} from '../featuresExtractor.ts';

import type {
	useReadTimeQuery$data,
	useReadTimeQuery$variables,
} from './__generated__/useReadTimeQuery.graphql.ts';

export const ReadTime_content: GraphQLTaggedNode = graphql`
	fragment useReadTime_content on Content {
		id
		type
		body {
			dynamic {
				value
				representation
			}
		}
		operations {
			operation
			targetType
		}
	}
`;

export const ReadTimeQuery: GraphQLTaggedNode = graphql`
	query useReadTimeQuery($contentId: ID, $status: [String], $includeContentBody: Boolean = true) {
		content(id: $contentId, status: $status) {
			nodes {
				id
				type
				body @include(if: $includeContentBody) {
					dynamic {
						value
						representation
					}
				}
				operations {
					operation
					targetType
				}
			}
		}
	}
`;

export const useReadTime = (
	contentId: string,
	skipQuery?: boolean,
): {
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	adf: JSONDocNode | undefined;
	contentType: string | null | undefined;
	readTime: number | null;
	wordCount: number | null;
	features: ContentFeatures | null;
	featuresSkipExpand: ContentFeatures | null;
	isReadTimeCapped: boolean;
} => {
	const readTimeQueryOptions = {
		variables: {
			contentId,
		},
		skip: skipQuery,
	};

	const { data, loading, error } = useQueryNoFurtherUpdateInterop<
		useReadTimeQuery$data,
		useReadTimeQuery$variables
	>(ReadTimeQuery, readTimeQueryOptions);

	const { adf, readTime, wordCount, features, featuresSkipExpand, isReadTimeCapped } =
		useMemo(() => {
			const empty = {
				readTime: null,
				wordCount: null,
				features: null,
				featuresSkipExpand: null,
				isReadTimeCapped: false,
			};

			let result;
			if (fg('read_time_parse_dedup')) {
				const adfString = getAdfString(data ?? undefined);
				if (!adfString || loading || error || !contentId) {
					result = empty;
				} else {
					let parsed: JSONDocNode | null;
					try {
						parsed = JSON.parse(adfString) as JSONDocNode;
					} catch {
						parsed = null;
					}
					if (!parsed) {
						result = empty;
					} else {
						const {
							features: allFeatures,
							featuresSkipExpand: allFeaturesSkipExpand,
							nodeCount,
						} = extractAllContentFeatures(parsed, adfString.length);
						const { readTime: computedReadTime, isReadTimeCapped: computedCapped } =
							readTimeFromFeatures(allFeaturesSkipExpand, nodeCount);
						if (computedCapped) {
							// The walk stopped early, so counts are partial — treat them as
							// unavailable and show only the "{floor}+ min read" lower bound.
							result = {
								adf: parsed,
								readTime: computedReadTime,
								wordCount: null,
								features: null,
								featuresSkipExpand: null,
								isReadTimeCapped: true,
							};
						} else {
							result = {
								adf: parsed,
								readTime: computedReadTime,
								wordCount: allFeaturesSkipExpand.wordCount,
								features: allFeatures,
								featuresSkipExpand: allFeaturesSkipExpand,
								isReadTimeCapped: false,
							};
						}
					}
				}
			} else {
				// fg OFF — existing path
				const adfFromData = getAdf(data ?? undefined);
				if (!adfFromData || loading || error || !contentId) {
					result = empty;
				} else {
					result = {
						adf: adfFromData,
						readTime: calculateEstimatedReadTime(adfFromData),
						wordCount: getWordCount(adfFromData),
						features: extractContentFeatures(adfFromData),
						featuresSkipExpand: extractContentFeaturesSkipExpand(adfFromData),
						isReadTimeCapped: false,
					};
				}
			}
			return result;
		}, [loading, error, data, contentId]);

	const contentNode = data?.content?.nodes?.[0];
	const contentType = contentNode?.type;

	return {
		loading,
		error,
		adf,
		contentType,
		readTime,
		wordCount,
		features,
		featuresSkipExpand,
		isReadTimeCapped,
	};
};
